import jsPDF from 'jspdf';
import { fmtDate } from './internshipTypes';

// Fields printed on the certificate — satisfied by both the authenticated InternshipCertificate
// and the public verification response, so the same PDF can be produced from either place.
export interface PrintableCertificate {
  certificateNo: string;
  certificateType: string;
  internName: string;
  roleTitle: string;
  department: string | null;
  startDate: string;
  endDate: string;
  issuedAt: string;
  verificationUrl: string;
  companyName: string;
  signatoryName: string;
  signatoryTitle: string;
}

// Generated client-side with jsPDF (same approach as payslips in MyPayslips.tsx). The PDF is a
// convenience copy — authenticity comes from the verification URL printed on it, which shows
// the server's record (and whether it has been revoked).
export const downloadInternshipCertificate = (c: PrintableCertificate) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const isCompletion = c.certificateType === 'Completion';

  // Double border
  doc.setDrawColor(25, 118, 210);
  doc.setLineWidth(1.5);
  doc.rect(8, 8, w - 16, h - 16);
  doc.setLineWidth(0.4);
  doc.rect(12, 12, w - 24, h - 24);

  doc.setTextColor(25, 118, 210);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(c.companyName, w / 2, 32, { align: 'center' });

  doc.setTextColor(40, 40, 40);
  doc.setFontSize(26);
  doc.text(isCompletion ? 'CERTIFICATE OF INTERNSHIP' : 'CERTIFICATE OF PARTICIPATION', w / 2, 52, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.text('This is to certify that', w / 2, 70, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text(c.internName, w / 2, 86, { align: 'center' });
  doc.setLineWidth(0.3);
  doc.line(w / 2 - 70, 90, w / 2 + 70, 90);

  const department = c.department ? ` in the ${c.department} department` : '';
  const body = isCompletion
    ? `has successfully completed an internship as ${c.roleTitle}${department} at ${c.companyName} ` +
      `from ${fmtDate(c.startDate)} to ${fmtDate(c.endDate)}.`
    : `has participated in an internship as ${c.roleTitle}${department} at ${c.companyName} ` +
      `from ${fmtDate(c.startDate)} to ${fmtDate(c.endDate)}.`;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.text(doc.splitTextToSize(body, w - 80), w / 2, 104, { align: 'center' });
  doc.text(`We wish ${c.internName} every success in all future endeavours.`, w / 2, 124, { align: 'center' });

  // Footer: certificate number + verification (left), signatory (right)
  const footerY = h - 40;
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(`Certificate No: ${c.certificateNo}`, 24, footerY);
  doc.text(`Issued on: ${fmtDate(c.issuedAt)}`, 24, footerY + 6);
  doc.text('Verify at:', 24, footerY + 12);
  doc.setTextColor(25, 118, 210);
  doc.textWithLink(c.verificationUrl, 24, footerY + 18, { url: c.verificationUrl });

  doc.setTextColor(40, 40, 40);
  doc.line(w - 94, footerY + 4, w - 24, footerY + 4);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(c.signatoryName, w - 59, footerY + 11, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`${c.signatoryTitle}, ${c.companyName}`, w - 59, footerY + 17, { align: 'center' });

  doc.save(`${c.certificateNo.replace(/\//g, '-')}_${c.internName.replace(/\s+/g, '_')}.pdf`);
};
